import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/forgot-password")({
  head: () =>
    seoHead({
      title: "パスワードをお忘れの方 | KAGEM",
      description: "KAGEMのパスワードリセット用メールを送信します。",
      path: "/forgot-password",
      noindex: true,
    }),
  component: ForgotPasswordPage,
});

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError("メールアドレスの形式が正しくありません");
      return;
    }
    setEmailError(null);
    setSubmitting(true);

    try {
      // Always show the same success message regardless of whether the
      // email exists, to prevent account enumeration.
      await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setSent(true);
    } catch {
      // Even on network errors, intentionally show the generic success state
      // to avoid leaking account existence. We also surface a soft toast.
      toast.error("ネットワークエラーが発生しました。接続をご確認ください");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-gradient-hero relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-10 sm:px-6">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Link to="/">
            <BrandLogo size="md" />
          </Link>
        </div>

        <Card className="border-border/60 shadow-2xl shadow-brand-primary/10 backdrop-blur-md">
          {sent ? (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <MailCheck className="h-6 w-6" />
                </div>
                <CardTitle className="text-2xl">メールを送信しました</CardTitle>
                <CardDescription>受信箱をご確認ください</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  入力されたメールアドレスにパスワードリセット用のリンクをお送りしました。メールが届かない場合は、迷惑メールフォルダもご確認ください。
                </p>
                <Button asChild variant="outline" className="w-full">
                  <a href="/login">ログインへ戻る</a>
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">パスワードをお忘れの方</CardTitle>
                <CardDescription>
                  ご登録のメールアドレス宛にリセット用のリンクをお送りします
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="email">メールアドレス</Label>
                    <Input
                      id="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError(null);
                      }}
                      aria-invalid={!!emailError}
                      required
                    />
                    {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                  </div>

                  <Button
                    type="submit"
                    className="h-11 w-full bg-gradient-brand text-white shadow-lg shadow-brand-primary/30 hover:opacity-95"
                    disabled={submitting || email.length === 0}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    リセットメールを送信
                  </Button>

                  <p className="text-center text-sm text-muted-foreground">
                    <a href="/login" className="text-primary underline-offset-4 hover:underline">
                      ログインへ戻る
                    </a>
                  </p>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
